/* Hauser MX — coreografía de la página Nosotros (Fase 10).
 *
 *   1. Hero      → la palabra clave se rellena (contorno → sólido) con el scroll.
 *   2. Manifiesto→ la frase entra por líneas y una palabra sustituye a otra.
 *   3. Terreno   → los municipios del mapa se encienden en secuencia.
 *   4. Criterio  → los iconos se dibujan solos al entrar en vista.
 *   5. Método    → el riel de pasos avanza gobernado por el scroll vertical.
 *
 * ── PRINCIPIO QUE GOBIERNA TODO ESTE ARCHIVO ───────────────────────────────
 * Nada de aquí es necesario para entender la página. Cada efecto SOLO añade;
 * ninguno esconde contenido esperando a activarse. Si este archivo no carga,
 * si GSAP falla o si el motor cae a `flat`, la página se lee completa: el
 * mapa llega pintado desde Liquid, la frase se ve entera y los pasos son un
 * riel arrastrable. Por eso no hay un solo `opacity: 0` de partida en el CSS
 * que dependa de que este JS lo revierta.
 *
 * La única excepción controlada es la segunda palabra del manifiesto, que sí
 * empieza oculta — pero la frase con la PRIMERA palabra está escrita para
 * sostenerse sola, y así se documenta en el ajuste del editor.
 */
(function () {
  'use strict';

  var H = window.Hauser;
  if (!H) return;

  var doc = document;

  /* ── 1. Hero: relleno de la palabra clave ─────────────────────────── */

  function heroRelleno() {
    var clave = doc.querySelector('[data-hauser-relleno]');
    if (!clave || H.tier === 'flat' || !window.gsap || !window.ScrollTrigger) return;

    H.media(function (ctx) {
      var st = window.ScrollTrigger.create({
        trigger: clave,
        scroller: ctx.scroller,
        start: 'top 85%',
        end: 'top 35%',
        scrub: true,
        onUpdate: function (self) {
          /* clip-path de derecha a izquierda: 100 % = nada relleno. */
          var restante = (1 - self.progress) * 100;
          clave.style.setProperty('--relleno', restante.toFixed(1) + '%');
        }
      });
      /* Estilo aplicado por variable para no pelearse con la regla del CSS. */
      clave.style.setProperty('--relleno', '100%');
      return function () {
        st.kill();
        clave.style.removeProperty('--relleno');
      };
    });
  }

  /* ── 2. Manifiesto: líneas y cambio de palabra ────────────────────── */

  function manifiesto() {
    var sec = doc.querySelector('[data-hauser-manifiesto]');
    if (!sec || H.tier === 'flat' || !window.gsap || !window.ScrollTrigger) return;

    var lineas = sec.querySelectorAll('.hauser-manifiesto__linea');
    if (!lineas.length) return;

    H.media(function (ctx) {
      var gsap = window.gsap;

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: sec,
          scroller: ctx.scroller,
          start: 'top 75%',
          once: true
        }
      });
      tl.from(lineas, {
        yPercent: 110,
        duration: 0.9,
        ease: 'expo.out',
        stagger: 0.12
      });

      /* El cambio de palabra ocurre a mitad del recorrido de la sección y se
         revierte al subir: el visitante que sube y baja lo ve las dos veces,
         que es justo lo que lo hace memorable. */
      var st = window.ScrollTrigger.create({
        trigger: sec,
        scroller: ctx.scroller,
        start: 'center 62%',
        onEnter: function () { sec.setAttribute('data-cambiado', 'true'); },
        onLeaveBack: function () { sec.removeAttribute('data-cambiado'); }
      });

      return function () {
        tl.kill();
        st.kill();
        gsap.set(lineas, { clearProps: 'all' });
        sec.removeAttribute('data-cambiado');
      };
    });
  }

  /* ── 3. Terreno: encendido en secuencia ───────────────────────────── */

  function terreno() {
    var sec = doc.querySelector('[data-hauser-terreno]');
    if (!sec) return;

    var items = Array.prototype.slice.call(sec.querySelectorAll('.hauser-terreno__item'));
    if (items.length < 2 || H.tier === 'flat' || !window.gsap || !window.ScrollTrigger) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return;

    /* Índice de slug → trazo y punto del mapa. Se resuelve UNA vez: consultar
       el DOM en cada cuadro de scroll por 33 municipios es gasto puro. */
    var mapa = {};
    items.forEach(function (li) {
      var slug = li.getAttribute('data-municipio');
      if (!slug) return;
      mapa[slug] = {
        item: li,
        muni: sec.querySelector('.hauser-mapa__muni[data-municipio="' + slug + '"]'),
        punto: sec.querySelector('.hauser-mapa__punto[data-municipio="' + slug + '"]')
      };
    });
    var slugs = Object.keys(mapa);
    if (!slugs.length) return;

    function foco(activo) {
      slugs.forEach(function (s) {
        var n = mapa[s];
        var on = s === activo;
        n.item.setAttribute('data-foco', on ? 'true' : 'false');
        if (n.muni) n.muni.setAttribute('data-foco', on ? 'true' : 'false');
        if (n.punto) n.punto.setAttribute('data-foco', on ? 'true' : 'false');
      });
    }

    H.media(function (ctx) {
      /* `data-seq` es el interruptor del CSS: solo cuando está puesto el mapa
         baja la saturación de los municipios no enfocados. Se pone AQUÍ, ya
         con el ScrollTrigger creado, para que nunca quede un mapa apagado
         porque el efecto no llegó a montarse. */
      sec.setAttribute('data-seq', 'true');
      foco(slugs[0]);

      var st = window.ScrollTrigger.create({
        trigger: sec,
        scroller: ctx.scroller,
        start: 'top 70%',
        end: 'bottom 60%',
        onUpdate: function (self) {
          var i = Math.min(slugs.length - 1, Math.floor(self.progress * slugs.length));
          foco(slugs[i]);
        }
      });

      return function () {
        st.kill();
        sec.removeAttribute('data-seq');
        slugs.forEach(function (s) {
          var n = mapa[s];
          n.item.removeAttribute('data-foco');
          if (n.muni) n.muni.removeAttribute('data-foco');
          if (n.punto) n.punto.removeAttribute('data-foco');
        });
      };
    });
  }

  /* ── 4. Criterio: iconos que se dibujan ───────────────────────────── */

  function criterio() {
    var sec = doc.querySelector('[data-hauser-criterio]');
    if (!sec || H.tier === 'flat') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var svgs = sec.querySelectorAll('.hauser-criterio__icono svg');
    Array.prototype.forEach.call(svgs, function (svg) {
      var trazos = svg.querySelectorAll('path, circle');
      if (!trazos.length) return;

      /* La longitud real de cada trazo se mide del propio SVG. Escribir un
         `stroke-dasharray` a ojo deja unos iconos a medio dibujar y otros con
         un salto al final, según lo largo que sea el path. */
      var ok = true;
      Array.prototype.forEach.call(trazos, function (t) {
        if (typeof t.getTotalLength !== 'function') { ok = false; return; }
        var largo = Math.ceil(t.getTotalLength());
        if (!largo) { ok = false; return; }
        t.style.setProperty('--largo', largo);
      });
      if (!ok) return;

      svg.setAttribute('data-trazo', '');
      H.enVista(svg, function () {
        svg.setAttribute('data-dibujado', '');
      });
    });
  }

  /* ── 5. Método: riel gobernado por el scroll ──────────────────────── */

  function metodo() {
    var sec = doc.querySelector('[data-hauser-metodo]');
    if (!sec || H.tier === 'flat' || !window.gsap || !window.ScrollTrigger) return;

    var riel = sec.querySelector('.hauser-metodo__riel');
    var pin = sec.querySelector('.hauser-metodo__pin');
    var avance = sec.querySelector('.hauser-metodo__barra-avance');
    if (!riel || !pin) return;

    H.media(function (ctx) {
      /* Solo en escritorio. En móvil el riel se arrastra con el dedo, que es
         mejor gesto que secuestrarle el scroll vertical. */
      if (!ctx.escritorio) {
        if (avance) {
          /* En móvil la barra refleja el arrastre real del riel. */
          var onScroll = function () {
            var max = riel.scrollWidth - riel.clientWidth;
            var p = max > 0 ? riel.scrollLeft / max : 0;
            avance.style.width = (p * 100).toFixed(1) + '%';
          };
          riel.addEventListener('scroll', onScroll, { passive: true });
          onScroll();
          return function () { riel.removeEventListener('scroll', onScroll); };
        }
        return;
      }

      var gsap = window.gsap;
      sec.setAttribute('data-riel', 'true');

      var distancia = function () {
        return Math.max(0, riel.scrollWidth - riel.clientWidth);
      };

      var tween = gsap.to(riel, {
        x: function () { return -distancia(); },
        ease: 'none',
        scrollTrigger: {
          trigger: sec,
          scroller: ctx.scroller,
          pin: pin,
          start: 'top top',
          end: function () { return '+=' + distancia(); },
          scrub: 0.6,
          invalidateOnRefresh: true,
          anticipatePin: 1,
          onUpdate: function (self) {
            if (avance) avance.style.width = (self.progress * 100).toFixed(1) + '%';
          }
        }
      });

      return function () {
        if (tween.scrollTrigger) tween.scrollTrigger.kill();
        tween.kill();
        gsap.set(riel, { clearProps: 'all' });
        sec.removeAttribute('data-riel');
        if (avance) avance.style.width = '';
      };
    });
  }

  /* ── arranque ─────────────────────────────────────────────────────── */

  H.listo(function () {
    [heroRelleno, manifiesto, terreno, criterio, metodo].forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* Un efecto roto no puede llevarse por delante a los otros cuatro. */
        if (window.console) console.warn('[Hauser] Nosotros, efecto con error:', e);
      }
    });
  });
})();
